import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import axios from 'axios';
import classNames from 'classnames';

import { decimalUpTo } from 'Helpers/currencyFormat';
import { formatAndRoundMoney } from 'Helpers/formatService';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import KeyStatTile from 'CommonComponents/KeyStatTile';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import TableSearchInput from 'CommonComponents/TableSearchInput';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import DownloadIcon from 'Assets/icons/download.svg';

const COLUMN_COUNT = 7;

const money = (value) => formatAndRoundMoney(value ?? 0);
const percent = (value) => `${decimalUpTo(Number(value ?? 0), 2)}%`;

/** The URL with the given query parameters set; a null or empty value removes the parameter. */
const withQuery = (url, params) => {
    const target = new URL(url, window.location.origin);

    Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
            target.searchParams.delete(key);
        } else {
            target.searchParams.set(key, value);
        }
    });

    return target.toString();
};

const sum = (lines) => Math.round(lines.reduce((total, line) => total + Number(line.tagged_value), 0) * 100) / 100;

/**
 * The groups narrowed to the lines whose label matches the search, with their
 * subtotals recomputed; empty groups drop out.
 */
const filterGroups = (groups, search) => {
    const query = search.trim().toLowerCase();

    if (query === '') {
        return groups;
    }

    const matches = (line) => line.label.toLowerCase().includes(query);

    return groups
        .map((group) => {
            const lines = group.lines.filter(matches);
            const costcentres = group.costcentres
                .map((costcentre) => {
                    const ccLines = costcentre.lines.filter(matches);

                    return { ...costcentre, lines: ccLines, tagged_value: sum(ccLines) };
                })
                .filter((costcentre) => costcentre.lines.length > 0);
            const all = [...lines, ...costcentres.flatMap((costcentre) => costcentre.lines)];

            return { ...group, lines, costcentres, tagged_value: sum(all), all };
        })
        .filter((group) => group.all.length > 0);
};

const totalOf = (groups) => {
    const lines = groups.flatMap((group) => [...group.lines, ...group.costcentres.flatMap((costcentre) => costcentre.lines)]);

    return {
        tagged_value: sum(lines),
        committed: Math.round(lines.reduce((total, line) => total + Number(line.committed), 0) * 100) / 100,
    };
};

function Header({ config, exportHref }) {
    return (
        <div className="tw-flex tw-flex-col tw-gap-y-2">
            <nav aria-label={$t('Breadcrumb')} className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-charcoal-75">
                <a href={config.codesetUrl} className="tw-text-blue hover:tw-underline">
                    {$t('Codeset')}
                </a>
                <span aria-hidden="true">&rsaquo;</span>
                <a href={config.codesetUrl} className="tw-text-blue hover:tw-underline">
                    {config.codeset.name}
                </a>
                <span aria-hidden="true">&rsaquo;</span>
                <span>{config.tag.name}</span>
            </nav>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <h1 className="tw-m-0 tw-text-2.5xl tw-font-semibold tw-text-charcoal">{config.tag.name}</h1>
                    {config.tag.code && (
                        <span className="tw-bg-blue-15 tw-text-blue tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-sm tw-font-semibold">{config.tag.code}</span>
                    )}
                    <span className="tw-bg-charcoal-15 tw-text-charcoal-75 tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium">
                        {$t('Budget tag · {{codeset}}', { codeset: config.codeset.name })}
                    </span>
                </div>
                <Button as="a" href={exportHref} variant="outline-neutral">
                    <DownloadIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                    {$t('Download')}
                </Button>
            </div>
        </div>
    );
}

function Tiles({ tiles }) {
    return (
        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-5 tw-gap-4">
            <KeyStatTile title={$t('Tagged lines')} value={tiles.tagged_lines} />
            <KeyStatTile title={$t('Tagged budget')} value={money(tiles.tagged_budget)} />
            <KeyStatTile title={$t('Committed')} value={money(tiles.committed)} />
            <KeyStatTile title={$t('Uncommitted')} value={money(tiles.uncommitted)} />
            <KeyStatTile title={$t('Departments')} value={<span className="tw-text-blue">{tiles.departments}</span>} />
        </div>
    );
}

/**
 * A filter pill reading "{label} {selected} ⌄", drawn like the outline-neutral button.
 * The label carries its own colon ("Tag:") so a translation can place it.
 * A native select sits over the pill's text so the browser's own picker and keyboard
 * handling are kept; SelectInput is not used because its select carries the grey
 * form-field fill.
 */
function FilterPill({ label, selectedLabel, value, onChange, children }) {
    return (
        <label className="tw-relative tw-mb-0 tw-inline-flex tw-h-8 tw-items-center tw-gap-x-1 tw-rounded-full tw-border tw-border-charcoal-15 tw-bg-white tw-ps-4 tw-pe-3 tw-text-sm tw-text-charcoal-75 focus-within:tw-ring-2 focus-within:tw-ring-blue hover:tw-bg-charcoal-10">
            <span>{label}</span>
            <span className="tw-font-semibold tw-text-charcoal">{selectedLabel}</span>
            <ChevronDownIcon className="tw-h-3 tw-w-3 tw-text-charcoal-75" aria-hidden="true" />
            <select aria-label={label} value={value} onChange={onChange} className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-cursor-pointer tw-opacity-0">
                {children}
            </select>
        </label>
    );
}

function Toolbar({ config, budgetId, budgetLabel, onTagChange, onVersionChange, search, onSearch }) {
    const ownTagOffered = config.tagOptions.some((group) => group.options.some((option) => option.value === config.tag.id));

    return (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                <FilterPill
                    // i18next reads a one-word key before ":" as a namespace and returns "", so the separator is off.
                    label={$t('Tag:', { nsSeparator: false })}
                    selectedLabel={config.tag.name}
                    value={ownTagOffered ? config.tag.id : ''}
                    onChange={(e) => onTagChange(Number(e.target.value))}
                >
                    {!ownTagOffered && (
                        <option value="" disabled>
                            {config.tag.name}
                        </option>
                    )}
                    {config.tagOptions.map((group) => (
                        <optgroup key={group.label} label={group.label}>
                            {group.options.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </FilterPill>
                {config.versions.length > 1 && (
                    <FilterPill
                        label={$t('Budget version:', { nsSeparator: false })}
                        selectedLabel={budgetLabel}
                        value={budgetId ?? ''}
                        onChange={(e) => onVersionChange(e.target.value)}
                    >
                        {config.versions.map((version) => (
                            <option key={version.value} value={version.value}>
                                {version.label}
                            </option>
                        ))}
                    </FilterPill>
                )}
            </div>
            <div className="tw-w-full sm:tw-w-64">
                <TableSearchInput searchValue={search} handleChange={(e) => onSearch(e.target.value)} placeholder={$t('Search')} />
            </div>
        </div>
    );
}

const cellClass = 'tw-border-b tw-border-grey tw-px-3 tw-py-2.5 tw-align-middle';
const numberClass = classNames(cellClass, 'tw-text-end tw-whitespace-nowrap');

function SubtotalRow({ name, value, indent }) {
    return (
        <tr className={classNames('tw-font-semibold tw-text-charcoal', { 'tw-bg-charcoal-10': !indent })}>
            <td className={classNames(cellClass, { 'tw-ps-6': indent })} colSpan={4}>
                {name}
            </td>
            <td className={numberClass}>{money(value)}</td>
            <td className={cellClass} colSpan={2} />
        </tr>
    );
}

function LineRow({ line }) {
    return (
        <tr className="tw-text-charcoal">
            <td className={classNames(cellClass, 'tw-ps-10')}>{line.label}</td>
            <td className={cellClass}>{line.type_label}</td>
            <td className={numberClass}>{money(line.line_value)}</td>
            <td className={numberClass}>{percent(line.percentage)}</td>
            <td className={numberClass}>{money(line.tagged_value)}</td>
            <td className={numberClass}>{money(line.committed)}</td>
            <td className={classNames(cellClass, 'tw-text-xs tw-text-charcoal-50')}>{line.source_label}</td>
        </tr>
    );
}

function TagTable({ groups, total, emptyMessage }) {
    const headClass = 'tw-border-b tw-border-grey tw-px-3 tw-py-2.5 tw-text-xs tw-font-medium tw-text-charcoal-75 tw-whitespace-nowrap';

    return (
        <div className="tw-overflow-x-auto tw-rounded-lg tw-border-2 tw-border-grey">
            {/* Fixed widths keep the columns put across versions, searches and the empty state. */}
            <table className="tw-w-full tw-min-w-[900px] tw-table-fixed tw-border-separate tw-border-spacing-0 tw-text-sm">
                <colgroup>
                    <col />
                    <col className="tw-w-32" />
                    <col className="tw-w-32" />
                    <col className="tw-w-20" />
                    <col className="tw-w-32" />
                    <col className="tw-w-32" />
                    <col className="tw-w-44" />
                </colgroup>
                <thead>
                    <tr>
                        <th className={classNames(headClass, 'tw-text-start')}>{$t('Line')}</th>
                        <th className={classNames(headClass, 'tw-text-start')}>{$t('Type')}</th>
                        <th className={classNames(headClass, 'tw-text-end')}>{$t('Line value')}</th>
                        <th className={classNames(headClass, 'tw-text-end')}>{$t('Tag %')}</th>
                        <th className={classNames(headClass, 'tw-text-end')}>{$t('Tagged value')}</th>
                        <th className={classNames(headClass, 'tw-text-end')}>{$t('Committed')}</th>
                        <th className={classNames(headClass, 'tw-text-start')}>{$t('Tag source')}</th>
                    </tr>
                </thead>
                <tbody>
                    {groups.length === 0 && (
                        <tr>
                            <td colSpan={COLUMN_COUNT} className={classNames(cellClass, 'tw-py-6 tw-text-center tw-text-charcoal-75')}>
                                {emptyMessage}
                            </td>
                        </tr>
                    )}
                    {groups.map((group) => (
                        <React.Fragment key={group.id}>
                            <SubtotalRow name={group.name} value={group.tagged_value} />
                            {group.lines.map((line) => (
                                <LineRow key={line.key} line={line} />
                            ))}
                            {group.costcentres.map((costcentre) => (
                                <React.Fragment key={costcentre.id}>
                                    <SubtotalRow name={costcentre.name} value={costcentre.tagged_value} indent />
                                    {costcentre.lines.map((line) => (
                                        <LineRow key={line.key} line={line} />
                                    ))}
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
                <tfoot>
                    <tr className="tw-font-semibold tw-text-charcoal">
                        <td className="tw-border-t-2 tw-border-charcoal-25 tw-px-3 tw-py-2.5" colSpan={4}>
                            {$t('Total')}
                        </td>
                        <td className="tw-border-t-2 tw-border-charcoal-25 tw-px-3 tw-py-2.5 tw-text-end tw-whitespace-nowrap">{money(total.tagged_value)}</td>
                        <td className="tw-border-t-2 tw-border-charcoal-25 tw-px-3 tw-py-2.5 tw-text-end tw-whitespace-nowrap">{money(total.committed)}</td>
                        <td className="tw-border-t-2 tw-border-charcoal-25" />
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

/**
 * The lines one budget tag covers in a budget version, valued at the tag's
 * percentage and grouped by department and cost centre. Search narrows the table
 * only; the tiles always describe the whole item.
 */
export default function BudgetTagView({ config }) {
    // The version on screen; it only changes once that version's data has loaded.
    const [budgetId, setBudgetId] = useState(config.budgetId);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const latestRequest = useRef(0);

    const load = (nextBudgetId) => {
        const request = ++latestRequest.current;
        setLoading(true);
        setError(null);

        return axios
            .get(withQuery(config.dataUrl, { 'budget-id': nextBudgetId }), { headers: { Accept: 'application/json' } })
            .then((response) => {
                if (request !== latestRequest.current) {
                    return;
                }

                setData(response.data.data);
                setBudgetId(nextBudgetId);

                if (nextBudgetId !== null) {
                    window.history.replaceState(null, '', withQuery(window.location.href, { 'budget-id': nextBudgetId }));
                }
            })
            .catch(() => request === latestRequest.current && setError($t('The tag view could not be loaded. Please try again.')))
            .finally(() => request === latestRequest.current && setLoading(false));
    };

    useEffect(() => {
        load(config.budgetId);
    }, []);

    const changeTag = (tagId) => {
        const option = config.tagOptions.flatMap((group) => group.options).find((candidate) => candidate.value === tagId);

        if (option) {
            window.location.href = withQuery(option.url, { 'budget-id': budgetId });
        }
    };

    const groups = data ? filterGroups(data.groups, search) : [];
    const total = data && search.trim() === '' ? data.total : totalOf(groups);
    const emptyMessage = data?.is_empty ? $t('No lines tagged with {{item}}', { item: config.tag.name }) : $t('No lines match your search.');

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-6">
            <Header config={config} exportHref={withQuery(config.exportUrl, { 'budget-id': budgetId, search: search.trim() })} />

            {error && <Alert type="error">{error}</Alert>}

            {loading && !data ? (
                <LoadingSpinner />
            ) : (
                data && (
                    <>
                        <Tiles tiles={data.tiles} />
                        <Toolbar
                            config={config}
                            budgetId={budgetId}
                            onTagChange={changeTag}
                            budgetLabel={data.budget.label}
                            onVersionChange={load}
                            search={search}
                            onSearch={setSearch}
                        />
                        <div className={classNames({ 'tw-opacity-50': loading })}>
                            <TagTable groups={groups} total={total} emptyMessage={emptyMessage} />
                        </div>
                        <p className="tw-m-0 tw-text-xs tw-text-charcoal-75">
                            {$t(
                                'Tags are frozen with each budget version. Selecting a version shows the tags as they stood when it was locked; the Latest Estimate shows current tags.',
                            )}
                        </p>
                    </>
                )
            )}
        </div>
    );
}

const rootEl = document.getElementById('BudgetTagView');
if (rootEl) {
    ReactDOM.render(
        <App>
            <BudgetTagView config={JSON.parse(rootEl.getAttribute('data-config') || '{}')} />
        </App>,
        rootEl,
    );
}
